import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, FileSearch, Scale } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Read (not consume) the sign-out reason: a hard redirect can remount this page right after.
  const [notice] = useState(() => sessionStorage.getItem("signout_reason"));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/research");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/research";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { Icon: ShieldCheck, t: "Source-grounded, citation-first answers" },
    { Icon: FileSearch, t: "Search across every active guideline version" },
    { Icon: Scale, t: "Conflict and insufficient-evidence warnings" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-background" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-[#05304F] text-white p-12 xl:p-16 relative overflow-hidden" data-testid="login-brand-panel">
        <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full border-[40px] border-white/[0.04]" aria-hidden="true" />
        <div className="flex items-center gap-3">
          <img src={BRAND.logos.mark} alt="" className="h-9 w-9 object-contain" />
          <span className="text-sm font-medium text-white/80">{BRAND.company}</span>
        </div>
        <div className="relative max-w-lg">
          <h1 className="text-5xl xl:text-6xl font-semibold tracking-tight">{BRAND.shortName}</h1>
          <p className="mt-3 text-lg font-medium text-[#E0A430]">{BRAND.tagline}</p>
          <p className="mt-6 text-base leading-7 text-white/80">
            Grounded answers from your lender and agency guideline library. Every claim is cited to
            document, section, and page. If the source is unclear, Candi says so.
          </p>
          <div className="mt-10 space-y-3">
            {features.map(({ Icon, t }) => (
              <div key={t} className="flex items-center gap-3 rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm">
                <span className="h-8 w-8 rounded-lg bg-[#E0A430]/15 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-[#E0A430]" />
                </span>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/50">Internal tool · Authorized users only</div>
      </div>

      <div className="flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <div className="bg-white border border-[#E1E3E5] rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6 sm:p-8" data-testid="login-card">
            <img src={BRAND.logos.stacked} alt={BRAND.company} className="h-20 mb-6 object-contain" />
            <h2 className="text-2xl font-semibold text-[#202223]">Sign in</h2>
            <p className="text-[#6D7175] text-sm mt-1">Access the guideline research workspace.</p>
            {notice && (
              <div className="mt-4 rounded-lg border border-[#FFD6A5] bg-[#FFF8E6] px-3 py-2 text-sm text-[#4A2C00]" data-testid="signout-notice">{notice}</div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="h-10 rounded-lg" data-testid="login-email-input" placeholder="you@freedmont.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                       className="h-10 rounded-lg" data-testid="login-password-input" placeholder="••••••••" />
              </div>
              <Button type="submit" className="w-full h-10 rounded-lg bg-[#05304F] hover:bg-[#005180]" disabled={busy} data-testid="login-submit-button">
                {busy ? "Signing in..." : "Sign in"}
              </Button>
            </form>

            <div className="mt-6 flex items-center gap-3 text-xs text-[#8C9196]">
              <span className="h-px flex-1 bg-[#E1E3E5]" />
              or
              <span className="h-px flex-1 bg-[#E1E3E5]" />
            </div>

            <Button
              type="button"
              variant="outline"
              className="mt-6 w-full h-10 gap-2 rounded-lg bg-white border-[#C9CCCF] hover:bg-[#F1F2F4]"
              onClick={googleSignIn}
              data-testid="login-google-button"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
              Sign in with Google
            </Button>
            <p className="mt-3 text-xs text-[#6D7175]">
              Google sign-in works only for accounts an admin has already added.
            </p>
          </div>
          <p className="lg:hidden mt-4 text-center text-xs text-[#8C9196]">Internal tool · Authorized users only</p>
        </div>
      </div>
    </div>
  );
}
